import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area,
} from 'recharts';
import {
  Gamepad2, TrendingUp, Users, Clock, Trophy, RefreshCw,
  Target, Coins, Zap, BarChart3, Play, Film, Heart, AlertTriangle,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { adminService } from '../services/adminService';
import { useToast } from '../contexts/ToastContext';
import './Games.css';

const TIME_RANGES = [
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

const CHART_COLORS = ['#410C6D', '#22c55e', '#f59e0b', '#ef4444', '#5a1494', '#ec4899'];

const DARK_TOOLTIP = {
  backgroundColor: '#1f2937',
  border: 'none',
  borderRadius: '8px',
  color: '#fff',
};

const safeNum = (val: any, fallback = 0): number => {
  const n = parseFloat(val);
  return isNaN(n) ? fallback : n;
};

const fmtDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
};

const fmtPct = (val: any): string => {
  const n = safeNum(val);
  return n > 0 ? `${n.toFixed(1)}%` : 'N/A';
};

const fmtNumber = (val: any): string => {
  const n = safeNum(val);
  return n > 0 ? n.toLocaleString() : '0';
};

const safeDateLabel = (d: any): string => {
  try {
    return format(parseISO(d.date || d.day), 'MMM dd');
  } catch {
    return d.date || d.day || '';
  }
};

const Games: React.FC = () => {
  const toast = useToast();
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [gameData, setGameData] = useState<any>(null);
  const [quizData, setQuizData] = useState<any>(null);
  const [reviveData, setReviveData] = useState<any>(null);
  const [adsData, setAdsData] = useState<any>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const [gameRes, quizRes, reviveRes, adsRes] = await Promise.allSettled([
      adminService.getGameAnalytics(days),
      adminService.getQuizAnalytics(days),
      adminService.getReviveAnalytics(days),
      adminService.getRewardedAdsAnalytics(days),
    ]);

    let anySuccess = false;

    if (gameRes.status === 'fulfilled') {
      setGameData(gameRes.value);
      anySuccess = true;
    } else {
      setGameData(null);
      console.error('Game analytics failed:', gameRes.reason);
    }

    if (quizRes.status === 'fulfilled') {
      setQuizData(quizRes.value);
      anySuccess = true;
    } else {
      setQuizData(null);
      console.error('Quiz analytics failed:', quizRes.reason);
    }

    if (reviveRes.status === 'fulfilled') {
      setReviveData(reviveRes.value);
      anySuccess = true;
    } else {
      setReviveData(null);
      console.error('Revive analytics failed:', reviveRes.reason);
    }

    if (adsRes.status === 'fulfilled') {
      setAdsData(adsRes.value);
      anySuccess = true;
    } else {
      setAdsData(null);
      console.error('Rewarded ads analytics failed:', adsRes.reason);
    }

    if (!anySuccess) {
      setError('Failed to load analytics data. Please check your connection and try again.');
      toast.error('Analytics Error', 'Could not load any analytics data');
    }

    setLoading(false);
  }, [days, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // --- Derived stats ---
  const gameArray = Array.isArray(gameData) ? gameData : gameData?.daily || gameData?.data || [];
  const gameSummary = gameData?.summary || gameData || {};

  const totalGames = safeNum(gameSummary.total_games ?? gameSummary.total_sessions ?? gameArray.reduce((a: number, d: any) => a + safeNum(d.total_games), 0));
  const activePlayers = safeNum(gameSummary.active_players ?? gameSummary.unique_players ?? gameSummary.active_users);
  const avgDuration = safeNum(gameSummary.avg_session_duration ?? gameSummary.avg_duration ?? gameSummary.average_duration);
  const winRate = safeNum(gameSummary.win_rate ?? gameSummary.completion_rate);
  const totalTP = safeNum(gameSummary.total_tp_distributed ?? gameSummary.total_tp ?? gameSummary.total_trinze_points);

  const quizSummary = quizData?.summary || quizData || {};
  const quizAccuracy = safeNum(quizSummary.accuracy_rate ?? quizSummary.avg_accuracy ?? quizSummary.correct_rate);

  // Chart data: games per day
  const dailyGames = gameArray.map((d: any) => ({
    ...d,
    date: safeDateLabel(d),
    total: safeNum(d.total_games ?? d.total ?? d.count),
    completed: safeNum(d.completed_games ?? d.completed ?? d.wins),
  }));

  // Protocol distribution
  const demoCount = safeNum(gameSummary.demo_games ?? gameSummary.standard_games ?? gameArray.reduce((a: number, d: any) => a + safeNum(d.demo_games), 0));
  const multiplyCount = safeNum(gameSummary.multiply_games ?? gameSummary.advanced_games ?? gameArray.reduce((a: number, d: any) => a + safeNum(d.multiply_games), 0));
  const protocolDistribution = [
    { name: 'Demo', value: demoCount },
    { name: 'Multiply', value: multiplyCount },
  ].filter(d => d.value > 0);

  // Quiz difficulty distribution
  const quizDifficultyArray = quizData?.difficulty_distribution || quizData?.by_difficulty || [];
  const quizDifficulty = Array.isArray(quizDifficultyArray)
    ? quizDifficultyArray.map((d: any) => ({
        name: d.difficulty || d.level || d.name || 'Unknown',
        value: safeNum(d.count ?? d.total ?? d.questions),
      }))
    : Object.entries(quizDifficultyArray).map(([key, val]) => ({
        name: key,
        value: safeNum(val),
      }));

  // TP flow over time
  const tpFlowArray = gameData?.tp_flow || gameData?.daily || gameArray;
  const tpFlowData = Array.isArray(tpFlowArray)
    ? tpFlowArray.map((d: any) => ({
        date: safeDateLabel(d),
        distributed: safeNum(d.tp_distributed ?? d.total_tp ?? d.trinze_points),
        earned: safeNum(d.tp_earned ?? d.earned),
      }))
    : [];

  // Revive stats
  const reviveSummary = reviveData?.summary || reviveData || {};
  const reviveUsage = safeNum(reviveSummary.total_revives ?? reviveSummary.total_uses ?? reviveSummary.count);
  const reviveSuccessRate = safeNum(reviveSummary.success_rate ?? reviveSummary.completion_rate);
  const reviveDaily = Array.isArray(reviveData?.daily || reviveData?.data) ? (reviveData.daily || reviveData.data) : [];

  // Ads stats
  const adsSummary = adsData?.summary || adsData || {};
  const adsCompleted = safeNum(adsSummary.completed ?? adsSummary.total_completed ?? adsSummary.completions);
  const adsCompletionRate = safeNum(adsSummary.completion_rate ?? adsSummary.success_rate);
  const adsRevenue = safeNum(adsSummary.total_revenue ?? adsSummary.revenue ?? adsSummary.ad_revenue);
  const adsRequested = safeNum(adsSummary.total_requested ?? adsSummary.requests ?? adsSummary.impressions);

  return (
    <div className="games-page">
      {/* Header */}
      <div className="games-header">
        <div className="header-content">
          <h1><Gamepad2 size={28} /> Game Analytics</h1>
          <p className="header-subtitle">Protocol session analytics and game performance metrics</p>
        </div>
        <div className="header-actions">
          <div className="time-range-buttons">
            {TIME_RANGES.map((tr) => (
              <button
                key={tr.days}
                className={`time-btn ${days === tr.days ? 'active' : ''}`}
                onClick={() => setDays(tr.days)}
              >
                {tr.label}
              </button>
            ))}
          </div>
          <button onClick={loadData} className="refresh-btn" disabled={loading}>
            <RefreshCw className={loading ? 'spin' : ''} size={18} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="analytics-error">
          <AlertTriangle size={20} />
          <span>{error}</span>
          <button onClick={loadData}>Retry</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="games-stats six-cols">
        <StatCard
          icon={<Gamepad2 size={24} />}
          value={fmtNumber(totalGames)}
          label="Total Games Played"
          color="#410C6D"
          loading={loading}
        />
        <StatCard
          icon={<Users size={24} />}
          value={fmtNumber(activePlayers)}
          label="Active Players"
          color="#22c55e"
          loading={loading}
        />
        <StatCard
          icon={<Clock size={24} />}
          value={avgDuration > 0 ? fmtDuration(avgDuration) : 'N/A'}
          label="Avg Session Duration"
          color="#f59e0b"
          loading={loading}
        />
        <StatCard
          icon={<Trophy size={24} />}
          value={fmtPct(winRate)}
          label="Win Rate"
          color="#5a1494"
          loading={loading}
        />
        <StatCard
          icon={<Coins size={24} />}
          value={totalTP > 0 ? totalTP.toLocaleString() : '0'}
          label="Total TP Distributed"
          color="#ec4899"
          loading={loading}
        />
        <StatCard
          icon={<Target size={24} />}
          value={fmtPct(quizAccuracy)}
          label="Quiz Accuracy Rate"
          color="#14b8a6"
          loading={loading}
        />
      </div>

      {/* Charts Row 1: Games per day + Protocol distribution */}
      <div className="charts-row">
        <div className="chart-card wide">
          <div className="chart-header">
            <h3><BarChart3 size={18} /> Games Per Day</h3>
          </div>
          <div className="chart-body">
            {loading ? (
              <div className="chart-skeleton" />
            ) : dailyGames.length === 0 ? (
              <div className="chart-empty">No game data available for this period</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={dailyGames}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <Tooltip contentStyle={DARK_TOOLTIP} />
                  <Legend />
                  <Bar dataKey="total" name="Total Games" fill="#410C6D" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="completed" name="Completed" fill="#22c55e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3><Play size={18} /> Protocol Distribution</h3>
          </div>
          <div className="chart-body">
            {loading ? (
              <div className="chart-skeleton" />
            ) : protocolDistribution.length === 0 ? (
              <div className="chart-empty">No protocol data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={protocolDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                  >
                    {protocolDistribution.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Charts Row 2: Quiz difficulty + TP flow */}
      <div className="charts-row">
        <div className="chart-card">
          <div className="chart-header">
            <h3><Target size={18} /> Quiz Difficulty Distribution</h3>
          </div>
          <div className="chart-body">
            {loading ? (
              <div className="chart-skeleton" />
            ) : quizDifficulty.length === 0 ? (
              <div className="chart-empty">No quiz difficulty data available</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={quizDifficulty}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                  >
                    {quizDifficulty.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[(idx + 2) % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="chart-card wide">
          <div className="chart-header">
            <h3><TrendingUp size={18} /> TP Flow Over Time</h3>
          </div>
          <div className="chart-body">
            {loading ? (
              <div className="chart-skeleton" />
            ) : tpFlowData.length === 0 ? (
              <div className="chart-empty">No TP flow data available</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={tpFlowData}>
                  <defs>
                    <linearGradient id="tpGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#5a1494" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#5a1494" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <Tooltip contentStyle={DARK_TOOLTIP} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="distributed"
                    name="TP Distributed"
                    stroke="#5a1494"
                    fill="url(#tpGradient)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="earned"
                    name="TP Earned"
                    stroke="#22c55e"
                    fill="none"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Revive & Ads Section */}
      <div className="section-title">
        <Zap size={20} />
        <h2>Revive Tools & Rewarded Ads</h2>
      </div>

      <div className="revive-ads-grid">
        {/* Revive Stats */}
        <div className="analytics-card">
          <div className="analytics-card-header">
            <Heart size={20} className="card-icon revive" />
            <h3>Revive Tool Usage</h3>
          </div>
          <div className="analytics-card-body">
            {loading ? (
              <div className="card-skeleton-group">
                <div className="metric-skeleton" />
                <div className="metric-skeleton" />
              </div>
            ) : (
              <>
                <div className="mini-stats-grid">
                  <div className="mini-stat">
                    <span className="mini-stat-value">{fmtNumber(reviveUsage)}</span>
                    <span className="mini-stat-label">Total Revives</span>
                  </div>
                  <div className="mini-stat">
                    <span className="mini-stat-value">{fmtPct(reviveSuccessRate)}</span>
                    <span className="mini-stat-label">Success Rate</span>
                  </div>
                  <div className="mini-stat">
                    <span className="mini-stat-value">{fmtNumber(reviveSummary.unique_users ?? reviveSummary.users ?? 0)}</span>
                    <span className="mini-stat-label">Unique Users</span>
                  </div>
                  <div className="mini-stat">
                    <span className="mini-stat-value">{fmtNumber(reviveSummary.avg_per_session ?? reviveSummary.avg_revives ?? 0)}</span>
                    <span className="mini-stat-label">Avg Per Session</span>
                  </div>
                </div>
                {reviveDaily.length > 0 && (
                  <div className="mini-chart">
                    <ResponsiveContainer width="100%" height={140}>
                      <LineChart data={reviveDaily.map((d: any) => ({ date: safeDateLabel(d), count: safeNum(d.count ?? d.total ?? d.revives) }))}>
                        <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} width={30} />
                        <Tooltip contentStyle={DARK_TOOLTIP} />
                        <Line type="monotone" dataKey="count" stroke="#ec4899" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Ads Stats */}
        <div className="analytics-card">
          <div className="analytics-card-header">
            <Film size={20} className="card-icon ads" />
            <h3>Rewarded Ads</h3>
          </div>
          <div className="analytics-card-body">
            {loading ? (
              <div className="card-skeleton-group">
                <div className="metric-skeleton" />
                <div className="metric-skeleton" />
              </div>
            ) : (
              <>
                <div className="mini-stats-grid">
                  <div className="mini-stat">
                    <span className="mini-stat-value">{fmtNumber(adsCompleted)}</span>
                    <span className="mini-stat-label">Completions</span>
                  </div>
                  <div className="mini-stat">
                    <span className="mini-stat-value">{fmtPct(adsCompletionRate)}</span>
                    <span className="mini-stat-label">Completion Rate</span>
                  </div>
                  <div className="mini-stat">
                    <span className="mini-stat-value">{fmtNumber(adsRequested)}</span>
                    <span className="mini-stat-label">Requested</span>
                  </div>
                  <div className="mini-stat">
                    <span className="mini-stat-value">
                      {adsRevenue > 0 ? `₦${adsRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '₦0.00'}
                    </span>
                    <span className="mini-stat-label">Ad Revenue</span>
                  </div>
                </div>
                <div className="ad-completion-bar">
                  <div className="completion-bar-track">
                    <div
                      className="completion-bar-fill"
                      style={{ width: `${Math.min(safeNum(adsCompletionRate), 100)}%` }}
                    />
                  </div>
                  <span className="completion-bar-label">
                    {fmtPct(adsCompletionRate)} completion rate
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface StatCardProps {
  icon: React.ReactNode;
  value: string;
  label: string;
  color: string;
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ icon, value, label, color, loading }) => (
  <div className="stat-card" style={{ borderLeftColor: color }}>
    <div className="stat-icon" style={{ backgroundColor: `${color}15`, color }}>
      {icon}
    </div>
    <div className="stat-content">
      {loading ? (
        <div className="metric-skeleton" />
      ) : (
        <span className="stat-value">{value}</span>
      )}
      <span className="stat-label">{label}</span>
    </div>
  </div>
);

export default Games;
